/**
 * Stock Planning's Export and Print actions.
 *
 * Split out of StockPlanningTab for the 500-line cap when CR-8193 added Print
 * and the all-suppliers grid. Export streams the grid from the server; the
 * supplier warehouse stock export (CR-7163) is a different report that needs
 * one supplier; Print loads every matching row and prints them.
 */
import { useCallback, useEffect, useState } from 'react';
import { callOffAPI } from '../../../lib/api';
import type { ColumnPreference, StockPlanningItem } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { toast } from 'sonner';
import { blobErrorMessage, EXPORT_MIME, saveBlob } from '../../../utils/fileDownload';
import {
  matchesPartPrefixFilter, PART_PREFIX_FILTER_OPTIONS, type PartPrefixFilter,
} from '../shared/partPrefixFilter';
import { ALL_SUPPLIERS, isAllSuppliers } from './allSuppliers';

export interface StockPlanningFiltersState {
  /** A supplier's company id, '' for none chosen, or ALL_SUPPLIERS. */
  supplierId: string;
  openPoDays: number;
  salCommittedEndDate: string;
  factoryFilter: string;
  commodityType: string;
}

/** Rows fetched per request when Print loads the whole list. */
const PRINT_PAGE_SIZE = 500;
/** Safety stop for Print: matches the all-suppliers export cap (50,000 rows). */
const PRINT_MAX_PAGES = 100;

/**
 * The grid's query parameters for the current filters, shared by the grid,
 * the export and Print so all three read the same rows.
 *
 * @param filters - the Stock Planning filter panel
 * @param search - the (debounced) part search
 * @returns the query parameters, without paging
 */
export function planningParams(
  filters: StockPlanningFiltersState, search: string,
): Record<string, string | number> {
  const params: Record<string, string | number> = { supplier_id: filters.supplierId };
  if (filters.openPoDays) params['open_po_days'] = filters.openPoDays;
  if (filters.salCommittedEndDate) params['sal_committed_end_date'] = filters.salCommittedEndDate;
  if (filters.factoryFilter) params['factory_filter'] = filters.factoryFilter;
  if (filters.commodityType) params['commodity_type'] = filters.commodityType;
  if (search) params['search'] = search;
  return params;
}

const today = () => new Date().toISOString().split('T')[0];

interface UseStockPlanningExportArgs {
  filters: StockPlanningFiltersState;
  /** The user's saved columns (Export sends the visible ones, in order). */
  columns: ColumnPreference[];
  search: string;
  partFilter: PartPrefixFilter;
}

/**
 * Export, supplier stock export and Print for the Stock Planning grid.
 *
 * @example
 *   const { exporting, printing, printItems, handleExport, handlePrint } =
 *     useStockPlanningExport({ filters, columns, search: debouncedSearch, partFilter });
 * @param args - the grid's filters, saved columns, search and Parts filter
 * @returns busy flags, the rows being printed (null when not printing) and the actions
 */
export function useStockPlanningExport({
  filters, columns, search, partFilter,
}: UseStockPlanningExportArgs) {
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printItems, setPrintItems] = useState<StockPlanningItem[] | null>(null);
  const allSuppliers = isAllSuppliers(filters.supplierId);

  const handleExport = useCallback(async (fileFormat: 'csv' | 'xlsx') => {
    if (!filters.supplierId) {
      toast.error('Pick a supplier before exporting.');
      return;
    }
    setExporting(true);
    try {
      // Send the *currently visible* columns in display order so the file
      // matches what's on screen — including hide/reorder choices made via
      // the Columns dropdown. The server adds Supplier on the combined grid.
      const visibleIds = columns
        .filter((c) => c.is_visible)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((c) => c.column_id)
        .join(',');
      const response = await callOffAPI.exportStockPlanning({
        file_format: fileFormat,
        supplier_id: allSuppliers ? ALL_SUPPLIERS : Number(filters.supplierId),
        columns: visibleIds,
        open_po_days: filters.openPoDays,
        sal_committed_end_date: filters.salCommittedEndDate || undefined,
        factory_filter: filters.factoryFilter || undefined,
        commodity_type: filters.commodityType || undefined,
        search: search || undefined,
      });
      const name = allSuppliers ? 'stock_planning_all_suppliers' : 'stock_planning';
      saveBlob(response.data, `${name}_${today()}.${fileFormat}`, EXPORT_MIME[fileFormat]);
      const matched = Number(response.headers?.['x-export-truncated']);
      if (matched > 0) {
        toast.warning(
          `Export cut short: ${matched.toLocaleString()} rows matched, more than one file holds. `
          + 'Narrow the filters to export the rest.',
        );
      } else {
        toast.success(`Exported ${fileFormat.toUpperCase()}`);
      }
    } catch (err: unknown) {
      toast.error(await blobErrorMessage(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  }, [filters, columns, search, allSuppliers]);

  /**
   * CR-7163 (c): the supplier's warehouse stock report, restored to Stock
   * Planning. Distinct from handleExport above — that exports this grid's
   * columns; this pulls the supplier's actual stock levels, the same file the
   * supplier gets from Stock Upload. One supplier only.
   */
  const handleExportSupplierStock = useCallback(async (fileFormat: 'csv' | 'xlsx') => {
    if (!filters.supplierId || allSuppliers) {
      toast.error('Pick a supplier before exporting.');
      return;
    }
    setExporting(true);
    try {
      const response = await callOffAPI.exportStock(fileFormat, Number(filters.supplierId));
      saveBlob(response.data, `supplier_stock_${today()}.${fileFormat}`, EXPORT_MIME[fileFormat]);
      toast.success(`Exported ${fileFormat.toUpperCase()}`);
    } catch (err: unknown) {
      toast.error(await blobErrorMessage(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  }, [filters.supplierId, allSuppliers]);

  /** CR-8193: load every row the filters match — not just this page — then print. */
  const handlePrint = useCallback(async () => {
    if (!filters.supplierId) return;
    setPrinting(true);
    try {
      const params = planningParams(filters, search);
      let rows: StockPlanningItem[] = [];
      let total = Infinity;
      for (let page = 1; rows.length < total && page <= PRINT_MAX_PAGES; page += 1) {
        const { data } = await callOffAPI.getStockPlanning({ ...params, page, page_size: PRINT_PAGE_SIZE });
        total = data.total_count;
        if (data.items.length === 0) break;
        rows = rows.concat(data.items);
      }
      const cutShort = total > rows.length;
      rows = rows.filter((it) => matchesPartPrefixFilter(partFilter, it.part_number, it.description));
      if (rows.length === 0) {
        toast.error('Nothing to print — no rows match the filters.');
        return;
      }
      if (cutShort) {
        toast.warning(
          `Printing the first ${(PRINT_PAGE_SIZE * PRINT_MAX_PAGES).toLocaleString()} of `
          + `${total.toLocaleString()} rows. Narrow the filters to print the rest.`,
        );
      }
      setPrintItems(rows);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Could not load the rows to print'));
    } finally {
      setPrinting(false);
    }
  }, [filters, search, partFilter]);

  // Once the sheet has rendered, open the print dialog; drop the sheet after.
  useEffect(() => {
    if (!printItems) return undefined;
    const done = () => setPrintItems(null);
    window.addEventListener('afterprint', done, { once: true });
    const frame = window.requestAnimationFrame(() => window.print());
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('afterprint', done);
    };
  }, [printItems]);

  return {
    exporting, printing, printItems, handleExport, handleExportSupplierStock, handlePrint,
  };
}

/**
 * The filters in force, in one line for the printed sheet's header.
 *
 * @param filters - the Stock Planning filter panel
 * @param search - the part search
 * @param partFilter - the Parts filter
 * @returns e.g. 'Open PO days 7 · SAL committed to 2026-10-04 · Search "bracket"'
 */
export function printFilterSummary(
  filters: StockPlanningFiltersState, search: string, partFilter: PartPrefixFilter,
): string {
  const parts = [`Open PO days ${filters.openPoDays}`];
  if (filters.salCommittedEndDate) parts.push(`SAL committed to ${filters.salCommittedEndDate}`);
  if (filters.commodityType) parts.push(`Commodity ${filters.commodityType}`);
  if (search) parts.push(`Search "${search}"`);
  if (partFilter !== 'all') {
    const option = PART_PREFIX_FILTER_OPTIONS.find((o) => o.value === partFilter);
    if (option) parts.push(option.label);
  }
  return parts.join(' · ');
}
